import {
 Box,
 Checkbox,
 CircularProgress,
 FormControlLabel,
 FormGroup,
 Switch,
 Typography,
} from '@mui/material';
import { AccessGroupsDTO, GroupDTO } from '@redux/identity/identityTypes';
import { useTranslation } from 'react-i18next';

interface UserSettingsSwitchesProps {
 lockoutEnabled: boolean;
 twoFAEnabled: boolean;
 onLockoutToggle: (checked: boolean) => void;
 onTwoFAToggle: (checked: boolean) => void;
 disabled?: boolean;
}

export const UserSettingsSwitches = ({
 lockoutEnabled,
 twoFAEnabled,
 onLockoutToggle,
 onTwoFAToggle,
 disabled = false,
}: UserSettingsSwitchesProps) => {
 const { t } = useTranslation();

 return (
  <Box sx={{ mt: '24px' }}>
   <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, pb: 1 }}>
    <Typography sx={{ fontSize: 14, fontWeight: 500 }}>
     {t('user.settings')}
    </Typography>
   </Box>
   <FormGroup>
    <FormControlLabel
     control={
      <Switch
       checked={lockoutEnabled}
       onChange={(e) => onLockoutToggle(e.target.checked)}
       disabled={disabled}
       aria-label={t('user.sidebar.settings.toggleLockoutAriaLabel')}
      />
     }
     label={
      <Typography sx={{ fontSize: 12 }}>
       {t('user.sidebar.settings.blockUserFor100Years')}
      </Typography>
     }
    />
    <FormControlLabel
     control={
      <Switch
       checked={twoFAEnabled}
       onChange={(e) => onTwoFAToggle(e.target.checked)}
       disabled={disabled}
       aria-label={t('user.sidebar.settings.toggleTwoFactorAriaLabel')}
      />
     }
     label={
      <Typography sx={{ fontSize: 12 }}>
       {t('user.sidebar.settings.twoFactorEnabled')}
      </Typography>
     }
    />
   </FormGroup>
  </Box>
 );
};

interface UserAccessGroupsSectionProps {
 groups?: GroupDTO[] | null | string;
 accessGroups?: AccessGroupsDTO[];
 selectedGroupIds: number[];
 selectedAccessGroupIds: string[];
 onGroupToggle: (groupId: number, isChecked: boolean) => void;
 onAccessGroupToggle: (accessGroupId: string, isChecked: boolean) => void;
 loadingGroups?: boolean;
 loadingAccessGroups?: boolean;
 disabled?: boolean;
}

export const UserAccessGroupsSection = ({
 groups,
 accessGroups,
 selectedGroupIds,
 selectedAccessGroupIds,
 onGroupToggle,
 onAccessGroupToggle,
 loadingGroups = false,
 loadingAccessGroups = false,
 disabled = false,
}: UserAccessGroupsSectionProps) => {
 const { t } = useTranslation();
 return (
  <Box sx={{ p: 0 }}>
   <Typography sx={{ fontSize: 14, fontWeight: 500, pt: 3, pb: 1 }}>
    {t('user.sidebar.groups.userGroups')}
   </Typography>
   {loadingGroups && (
    <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
     <CircularProgress size={24} />
    </Box>
   )}
   {!loadingGroups && Array.isArray(groups) && (
    <FormGroup
     sx={{
      py: 1,
      px: 2,
      border: '1px solid',
      borderColor: (theme) => theme.palette.kemek.lightestGray,
      borderRadius: '4px',
     }}
    >
     {groups?.map((group) => (
      <FormControlLabel
       key={group.Id}
       control={
        <Checkbox
         checked={selectedGroupIds.includes(group.Id)}
         onChange={(e) => onGroupToggle(group.Id, e.target.checked)}
         disabled={disabled}
        />
       }
       label={
        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
         <Typography
          sx={{
           fontSize: 12,
           color: selectedGroupIds.includes(group.Id)
            ? (theme) => theme.palette.kemek.blue
            : (theme) => theme.palette.kemek.darkGray,
          }}
         >
          {group.Name ||
           `${t('user.sidebar.groups.groupFallback')} ${group.Id}`}
         </Typography>
         {group.Description && (
          <Typography
           sx={{ fontSize: 12, color: (theme) => theme.palette.kemek.darkGray }}
          >
           {group.Description}
          </Typography>
         )}
        </Box>
       }
      />
     ))}
    </FormGroup>
   )}
   <Typography sx={{ fontSize: 14, fontWeight: 500, pt: 3, pb: 1 }}>
    {t('user.sidebar.groups.accessGroups')}
   </Typography>
   {loadingAccessGroups ||
    (!Array.isArray(accessGroups) && (
     <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
      <CircularProgress size={24} />
     </Box>
    ))}
   {!loadingAccessGroups && (
    <FormGroup
     sx={{
      py: 1,
      px: 2,
      border: '1px solid',
      borderColor: (theme) => theme.palette.kemek.lightestGray,
      borderRadius: '4px',
     }}
    >
     {accessGroups?.map((accessGroup) => {
      const accessGroupId = accessGroup.Id || '';
      return (
       <FormControlLabel
        key={accessGroupId}
        control={
         <Checkbox
          checked={selectedAccessGroupIds.includes(accessGroupId)}
          onChange={(e) => onAccessGroupToggle(accessGroupId, e.target.checked)}
          disabled={disabled}
         />
        }
        label={
         <Typography
          sx={{
           fontSize: 12,
           color: selectedAccessGroupIds.includes(accessGroupId)
            ? (theme) => theme.palette.kemek.blue
            : (theme) => theme.palette.kemek.darkGray,
          }}
         >
          {accessGroup.Name ||
           `${t('user.sidebar.groups.accessGroupFallback')} ${accessGroup.Id}`}
         </Typography>
        }
       />
      );
     })}
    </FormGroup>
   )}
  </Box>
 );
};
